/**
 * renderAttribute — annotative-aware ATTDEF child renderer (PR-11).
 *
 * ATTDEFs are not top-level entities — they live inside `BlockDefinition.entities`
 * (see `attdefEntitySchema` in packages/drawing-model/src/model.ts). The
 * scene's per-INSERT walker (`renderBlockChildEntity` in blockRender.tsx)
 * historically inlined the ATTDEF render as a `<text>` element. PR-11 lifts
 * that path into this dedicated module so the render can consult the
 * cannoscale resolver and apply the per-scale `attribute`-kind context's
 * insertion / rotation / effective-scale overrides — matching the same
 * pattern `renderText` / `renderMText` use for top-level entities (PR-3).
 *
 * Why a separate module instead of inlining in blockRender.tsx:
 *
 *   1. blockRender.tsx is already 780+ lines and approaches the
 *      file-size budget noted in CLAUDE.md.
 *   2. The annotative branch is non-trivial — height multiplication,
 *      visibility gate, optional alignment-point fallback — and benefits
 *      from independent unit tests against `useCannoscaleResolver`.
 *   3. Future PRs (BEDIT-mode preview, attribute-grip rendering) can
 *      reuse the same helper without re-importing the whole block walker.
 *
 * AutoCAD parity (Priority #1): mirrors AutoCAD's attribute display rules
 * for an ATTDEF inside an annotative block reference:
 *
 *   - The displayed value is the per-INSERT attribute value if present
 *     (looked up by ATTDEF handle, then by tag), falling back to the
 *     defaultValue, then the tag, then "?". Never blank.
 *   - When the host INSERT carries an annotative context for the active
 *     CANNOSCALE, the ATTDEF's annotativeContexts (kind: "attribute")
 *     supply the per-scale insertion + rotation. Master fields are
 *     used otherwise.
 *   - The "invisible" flag on the ATTDEF flags hides the value display
 *     in normal rendering — AutoCAD shows the tag in dim grey when
 *     ATTDISP=ON forces invisible attributes visible. We render the tag
 *     in square brackets so the user sees the structure.
 *   - Width factor and oblique angle from the ATTDEF schema are applied
 *     via SVG transform / CSS for PR-11. A future PR-12 wires a STYLE
 *     fontName lookup mirroring renderText's SHX/TTF dispatch.
 */

import * as React from "react";
import type { AttdefEntity } from "@dxf-ai-editor/drawing-model";

import { resolveCannoscaleContext } from "./cannoscale/useCannoscaleResolver";

/**
 * Render a single ATTDEF child as an SVG `<text>` element.
 *
 * Parameters mirror the call site inside `renderBlockChildEntity`:
 *
 *   @param child    - the ATTDEF entity (from BlockDefinition.entities).
 *   @param attrLookup - per-INSERT attribute value map, keyed by ATTDEF
 *                       handle (preferred) and tag (fallback). Built by
 *                       `parseAttributes` in blockRender.
 *   @param keyPrefix  - React `key` prefix passed by the parent walker.
 *   @param annotationScaleName - active CANNOSCALE name. Optional so the
 *                       legacy callers (preview, MLEADER content) keep
 *                       working without threading the prop through.
 *   @param annoAllVisible - header ANNOALLVISIBLE flag. Same default
 *                       semantics as `renderText` — `true` keeps
 *                       annotative ATTDEFs without a matching context
 *                       visible (AutoCAD's "show at all scales").
 */
export function renderAttribute(
  child: AttdefEntity,
  attrLookup: Map<string, string>,
  keyPrefix: string,
  annotationScaleName?: string | null,
  annoAllVisible: boolean = true,
): React.ReactNode {
  // PR-11: resolve the per-scale `attribute`-kind context. The resolver
  // returns `effectiveScale: 1` and `activeContext: null` for
  // non-annotative ATTDEFs (annotativeContexts.length === 0), so the
  // call site below behaves exactly like the pre-PR-11 inline render
  // for legacy block content.
  const annoCtx = resolveCannoscaleContext(
    child.annotativeContexts ?? [],
    annotationScaleName ?? null,
    annoAllVisible,
  );
  if (!annoCtx.isVisible) {
    return null;
  }

  // Per-scale insertion / rotation override the master fields when the
  // resolver matched a context. The `?? child.…` fall-throughs preserve
  // the master-field defaults for non-annotative ATTDEFs.
  const insertX = annoCtx.insertion?.x ?? child.insertionPoint.x;
  const insertY = annoCtx.insertion?.y ?? child.insertionPoint.y;
  const rotation = annoCtx.rotation ?? child.rotation;

  // Annotative ATTDEFs render at `paper_height × effectiveScale` model
  // units. Non-annotative ATTDEFs hit `effectiveScale = 1` so the
  // multiplication is a no-op (same as the original inline render).
  const baseHeight = child.height > 0 ? child.height : 2.5;
  const fontSize = baseHeight * annoCtx.effectiveScale;

  // AutoCAD's attribute-display rules. The lookup mirrors the original
  // inline render in renderBlockChildEntity:
  //
  //   1. Try the per-INSERT value by ATTDEF handle (DXF round-trip
  //      preserves handles, so this is the most stable key).
  //   2. Fall back to lookup by tag (legacy DXFs may emit ATTRIB without
  //      a back-pointer to the ATTDEF handle).
  //   3. Fall back to the ATTDEF's defaultValue (DXF group 1).
  //   4. Fall back to the tag itself.
  //   5. Final fallback "?" — never render blank (Priority #1).
  //
  // Invisible flag: when the ATTDEF flag bit 1 (invisible) is set, the
  // attribute is hidden from normal rendering. For BEDIT preview we
  // surface the tag in square brackets so the author still sees the
  // ATTDEF placeholder — matches AutoCAD's ATTDISP=ON behaviour.
  let displayValue =
    attrLookup.get(child.handle) ||
    attrLookup.get(child.tag) ||
    child.defaultValue ||
    child.tag ||
    "?";
  if (child.flags?.invisible) {
    displayValue = `[${child.tag || displayValue}]`;
  }

  // Build the SVG transform chain. Rotation is applied first (around the
  // insertion point); width-factor is applied as a non-uniform scale
  // around the same anchor so AutoCAD's per-attribute width factor
  // (DXF 41) is honoured. Oblique angle (DXF 51) maps to CSS
  // `font-style: oblique <deg>`. Width factor of 1 and oblique of 0 are
  // omitted to keep the transform string compact for the common case.
  const transforms: string[] = [];
  if (rotation !== 0) {
    transforms.push(`rotate(${-rotation} ${insertX} ${insertY})`);
  }
  if (child.widthFactor !== 1) {
    transforms.push(
      `translate(${insertX} ${insertY}) scale(${child.widthFactor} 1) translate(${-insertX} ${-insertY})`,
    );
  }
  const transformAttr =
    transforms.length > 0 ? transforms.join(" ") : undefined;

  const obliqueStyle =
    child.obliqueAngle !== 0
      ? { fontStyle: `oblique ${child.obliqueAngle}deg` as const }
      : undefined;

  return (
    <text
      key={`${keyPrefix}-att-${child.handle || child.tag}`}
      x={insertX}
      y={insertY}
      fontSize={fontSize}
      transform={transformAttr}
      style={obliqueStyle}
      data-attdef-tag={child.tag}
      data-attdef-handle={child.handle || undefined}
      className="drawing-stage-text-entity"
    >
      {displayValue}
    </text>
  );
}
